import axios from 'axios';
import { Project } from './types';
import { RcFile } from 'antd/es/upload/interface';

const API_BASE_URL = (window as any).API_ADDRESS || "";

type UploadableFile = RcFile | File;

const api = axios.create({
    baseURL: API_BASE_URL,
});

export const getChapterByNumberContent = async (projectID: string, side: 'left' | 'right', num: number): Promise<{
    head: string[];
    tail: string[];
    id: string;
    name: string;
}> => {
    const response = await api.get(`/api/v1/project/${projectID}/${side}/chapter/${num}`, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

export const getChapterContent = async (projectID: string, side: 'left' | 'right', chapterID: string): Promise<{
    head: string[];
    tail: string[];
}> => {
    const response = await api.get(`/api/v1/project/${projectID}/${side}/${chapterID}`, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

export const updateProject = async (projectID: string, project: Project): Promise<Project> => {
    const response = await api.patch(`/api/v1/project/${projectID}`, project, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

export const uploadLeftBook = async (file: UploadableFile): Promise<{
    project_id: string;
    left_book_name: string;
}> => {
    const formData = new FormData();
    formData.append('left', file);
    
    const response = await api.post('/api/v1/project', formData, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data',
        },
    });
    
    return response.data;
};

export const uploadRightBook = async (projectID: string, file: UploadableFile): Promise<{
    right_book_name: string;
    left_chapters: Array<{ id: string; name: string; }>;
    right_chapters: Array<{ id: string; name: string; }>;
}> => {
    const formData = new FormData();
    formData.append('right', file);
    
    const response = await api.post(`/api/v1/project/${projectID}/right`, formData, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'multipart/form-data',
        },
    });
    
    return response.data;
};

export const updateChapterOffsets = async (
    projectID: string,
    number: number,
    leftHeadOffset: number,
    leftTailOffset: number,
    rightHeadOffset: number,
    rightTailOffset: number
): Promise<Project> => {
    const response = await api.post(`/api/v1/project/${projectID}/chapter/${number}/offsets`, {
        left_head_offset: leftHeadOffset,
        left_tail_offset: leftTailOffset,
        right_head_offset: rightHeadOffset,
        right_tail_offset: rightTailOffset,
    }, {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });
    return response.data;
};

export interface PDFGenerationResult {
    in_progress: boolean;
    url: string;
}

export interface PDFGenerationCallbacks {
    onProgress?: () => void;
    onComplete?: (url: string) => void;
    onError?: (error: Error) => void;
}

const generateProjectPDFCall = (
    projectID: string,
    callbacks?: PDFGenerationCallbacks
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const eventSource = new EventSource(`${API_BASE_URL}/api/v1/project/${projectID}/pdf`);
        
        eventSource.onmessage = (event) => {
            try {
                const data: PDFGenerationResult = JSON.parse(event.data);
                
                if (data.in_progress) {
                    callbacks?.onProgress?.();
                } else {
                    eventSource.close();
                    callbacks?.onComplete?.(data.url);
                    resolve(data.url);
                }
            } catch (error) {
                eventSource.close();
                const parseError = new Error(`Failed to parse SSE data: ${error}`);
                callbacks?.onError?.(parseError);
                reject(parseError);
            }
        };
        
        eventSource.onerror = (error) => {
            eventSource.close();
            const sseError = new Error('SSE connection error');
            callbacks?.onError?.(sseError);
            reject(sseError);
        };
        
        return () => {
            eventSource.close();
        };
    });
};

export const generateProjectPDF = async (projectID: string): Promise<string> => {
    return generateProjectPDFCall(projectID);
};